import React, { useEffect } from 'react'
import styled from 'styled-components'
import media from 'css-in-js-media'

import SwiperCore, { Pagination, Autoplay } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

// install Swiper components
SwiperCore.use([Pagination, Autoplay])

// Import Swiper styles
import 'swiper/swiper.scss'
import { Variables } from '../styles/Variables'
import { StyledHeading } from './Heading'
import NoticeBar from './NoticeBar'

const HeroSlider = styled(Swiper)`
    height: 100%;
    background-color: ${Variables.color.primary};
`

const HeroSliderWrap = styled.div`
    height: 100vh;
    width: 100%;
    height: calc(var(--vh, 1vh) * 100);
    position: relative;

    .swiper-wrapper {
        transition-timing-function: cubic-bezier(0.29, 0.46, 0.1, 1);
    }

    + ${StyledHeading} {
        ${media('<desktop')} {
            padding-top: 80px;
            padding-bottom: 80px;
        }
    }
`

const SliderPagination = styled.div`
    position: absolute;
    bottom: 41px;
    right: 80px;
    display: flex;
    z-index: 10;

    ${media('<desktop')} {
        right: 30px;
    }

    span {
        width: 6px;
        height: 6px;
        margin: 0 12px;
        background: none;
        border: 0;
        position: relative;
        opacity: 1;
        cursor: pointer;

        :before {
            content: '';
            width: 6px;
            height: 6px;
            background-color: white;
            position: absolute;
            left: 50%;
            top: 50%;
            transition: 0.1s opacity ease-out, 0.1s transform ease-out;
            transform: translate3d(-50%, -50%, 0);
            border-radius: 10px;
            z-index: 1;
        }

        :after {
            content: '';
            width: 8px;
            height: 8px;
            border: 2px solid white;
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate3d(-50%, -50%, 0) scale(0.8);
            transition: 0.1s opacity ease-out;
            border-radius: 10px;
            opacity: 0;
        }

        :last-child {
            margin-right: 0;
        }
    }

    .swiper-pagination-bullet-active {
        background-color: transparent;

        :before {
            transform: translate3d(-50%, -50%, 0) scale(2);
            opacity: 0;
        }
        :after {
            transform: translate3d(-50%, -50%, 0) scale(1);
            opacity: 1;
        }
    }
`

const HeroBlock = ({ children, noticeBar }) => {
    const slideCount = children.length ? children.length : 0

    // Set Window height param to be used in CSS
    // (https://css-tricks.com/the-trick-to-viewport-units-on-mobile/)
    const setWindowHeight = () => {
        if (window) {
            let vh = window.innerHeight * 0.01
            document.documentElement.style.setProperty('--vh', `${vh}px`)
        }
    }

    useEffect(() => {
        setWindowHeight()
        // Update on Window resize
        let timeoutId = null
        const resizeListener = () => {
            clearTimeout(timeoutId)
            timeoutId = setTimeout(() => setWindowHeight(), 150)
        }
        window.addEventListener('resize', resizeListener)
        return () => {
            window.removeEventListener('resize', resizeListener)
        }
    }, [])

    return (
        <>
            <HeroSliderWrap>
                <NoticeBar message={noticeBar} />
                <HeroSlider
                    loop={slideCount > 1 && true}
                    loopAdditionalSlides={slideCount > 1 && 2}
                    speed={1000}
                    pagination={{
                        el: '.HeroPagination',
                        type: 'bullets',
                        clickable: true,
                    }}
                    simulateTouch={slideCount < 2 ? false : true}
                    autoplay={{ delay: 6000, disableOnInteraction: true }}
                >
                    {slideCount > 0 ? (
                        children.map((slide, index) => {
                            return <SwiperSlide key={index}>{slide}</SwiperSlide>
                        })
                    ) : (
                        <SwiperSlide>{children}</SwiperSlide>
                    )}
                    {slideCount > 0 && <SliderPagination className="HeroPagination swiper-pagination" />}
                </HeroSlider>
            </HeroSliderWrap>
        </>
    )
}

export default HeroBlock
